import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Book, Estimate, IsbnSuggestion, Me, PublicBook } from "../api";
import { useAuth } from "../auth";
import { ChapterStrip } from "../bookmark";
import { Devices } from "./Devices";
import { Card, ErrorNote, JobBadge, LegalBadges, Spinner, usd, useLoad } from "../ui";

export function LibraryPage({ me, onChange }: { me: Me; onChange: () => void }) {
  const { api } = useAuth();
  const books = useLoad(() => api.books(), [api]);

  // Poll while something is being processed.
  const busy = books.data?.some((b) => b.job_status === "queued" || b.job_status === "running");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(books.reload, 5000);
    return () => clearInterval(t);
  }, [busy, books.reload]);
  const pending = me.status === "pending";

  return (
    <div className="stack">
      <LibraryTabs active="own" />
      <p className="muted">Privé, tenzij je een boek deelt: dan staat het bij Openbare boeken.</p>
      <Takedowns />
      <ErrorNote error={books.error} />
      {books.loading && !books.data && <Spinner />}
      {books.data && books.data.length === 0 && (
        <p className="muted">
          {pending ? (
            <>
              Nog geen boeken. Bekijk intussen de <a href="#/openbaar">openbare boeken</a>.
            </>
          ) : (
            "Nog geen boeken. Voeg hieronder je eerste EPUB toe."
          )}
        </p>
      )}
      <ul className="book-list">
        {books.data?.map((b) => (
          <li key={b.id} className="book-row">
            <Link
              className="book-item"
              to={b.last_chapter_index !== null ? `/books/${b.id}` : `/books/${b.id}/beheer`}
            >
              <span className="book-title">{b.title}</span>
              <span className="muted small">
                {b.authors.join(", ")}
                {b.num_chapters ? ` · ${b.num_chapters} hoofdstukken` : ""}
              </span>
            </Link>
            <BookStrip book={b} />
            <div className="book-meta-row">
              <JobBadge status={b.job_status} stage={b.job_stage} notBefore={b.job_not_before} />
              <LegalBadges book={b} />
              <span className="row small">
                {b.last_chapter_index !== null && <Link to={`/books/${b.id}/opzoeken`}>Opzoeken</Link>}
                {b.last_chapter_index !== null && <Link to={`/books/${b.id}/wiki`}>Wiki</Link>}
                <Link className="book-manage" to={`/books/${b.id}/beheer`}>
                  Beheren
                </Link>
              </span>
            </div>
          </li>
        ))}
      </ul>
      {pending ? (
        <AwaitingApproval me={me} />
      ) : (
        <Upload
          credits={me.credits_remaining}
          onUploaded={() => {
            books.reload();
            onChange();
          }}
        />
      )}
      <Devices />
      <Account me={me} onChange={onChange} />
    </div>
  );
}

/** In place of the upload until an admin approves the account (uploads need approval). */
function AwaitingApproval({ me }: { me: Me }) {
  return (
    <Card>
      <h2>Je account wacht op goedkeuring</h2>
      <p className="muted">
        Welkom{me.display_name ? `, ${me.display_name}` : ""}! Een beheerder keurt nieuwe accounts
        handmatig goed en kent gratis boek-credits toe; daarna kun je hier boeken toevoegen. Tot die
        tijd kun je de <a href="#/openbaar">openbare boeken</a> lezen en opzoeken.
      </p>
    </Card>
  );
}

/** "Mijn boeken" next to "Openbare boeken". */
function LibraryTabs({ active }: { active: "own" | "public" }) {
  const tabs = [
    { id: "own", label: "Mijn boeken", href: "#/" },
    { id: "public", label: "Openbare boeken", href: "#/openbaar" },
  ];
  return (
    <nav className="tabs library-tabs" aria-label="Boeken">
      {tabs.map((t) => (
        <a
          key={t.id}
          href={t.href}
          className={`tab ${active === t.id ? "tab-active" : ""}`}
          aria-current={active === t.id ? "page" : undefined}
        >
          {t.label}
        </a>
      ))}
    </nav>
  );
}

/** Books other users have shared: look up, the story so far and the wiki, spoiler-free. */
export function PublicBooksPage() {
  const { api } = useAuth();
  const books = useLoad(() => api.publicBooks(), [api]);
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = books.data?.filter(
    (b) => !needle || `${b.title} ${b.authors.join(" ")}`.toLowerCase().includes(needle),
  );
  return (
    <div className="stack">
      <LibraryTabs active="public" />
      <p className="muted">
        Wiki's die hun eigenaar heeft gedeeld. Je eigen boekenlegger bepaalt wat je ziet, dus ook
        hier geen spoilers.
      </p>
      <ErrorNote error={books.error} />
      {books.loading && !books.data && <Spinner />}
      {books.data && books.data.length > 3 && (
        <input
          type="search"
          placeholder="Zoek op titel of schrijver"
          aria-label="Openbare boeken doorzoeken"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      {shown && shown.length === 0 && (
        <p className="muted">
          {needle ? "Geen boek gevonden." : "Er zijn nog geen openbare boeken."}
        </p>
      )}
      <ul className="book-list">
        {shown?.map((b) => <PublicBookRow key={b.id} book={b} />)}
      </ul>
    </div>
  );
}

function PublicBookRow({ book: b }: { book: PublicBook }) {
  const basis =
    b.rights_basis === "public_domain" ? "publiek domein" : b.rights_basis === "licensed" ? "licentie" : null;
  return (
    <li className="book-row">
      <Link className="book-item" to={`/books/${b.id}`}>
        <span className="book-title">{b.title}</span>
        <span className="muted small">
          {[b.authors.join(", "), b.num_chapters ? `${b.num_chapters} hoofdstukken` : ""]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </Link>
      <BookStrip book={b} />
      <div className="book-meta-row">
        {basis && (
          <span className="badge badge-ok" title={b.rights_basis_by === "owner" ? "Opgegeven door de eigenaar." : "Vastgesteld door beheerder."}>
            Gedeeld: {basis}
          </span>
        )}
        <span className="row small">
          <Link to={`/books/${b.id}/opzoeken`}>Opzoeken & het verhaal tot nu toe</Link>
          <Link to={`/books/${b.id}/wiki`}>Wiki</Link>
        </span>
      </div>
    </li>
  );
}

/** The book as a strip of chapters: read up to the reader's bookmark, or processing progress. */
type StripBook = Pick<Book, "id" | "num_chapters" | "first_chapter_index" | "last_chapter_index"> &
  Partial<Pick<Book, "job_status" | "job_progress_done" | "job_progress_total" | "job_error">>;

function BookStrip({ book }: { book: StripBook }) {
  const running = book.job_status === "running" && !!book.job_progress_total;
  if (running) {
    const total = book.job_progress_total ?? 1;
    const done = book.job_progress_done ?? 0;
    return (
      <div className="book-strip-wrap">
        <ChapterStrip
          count={total}
          cls={(i, n) => {
            const at = Math.floor((done / total) * n);
            return i < at ? "done" : i === at ? "head" : "";
          }}
        />
        <p className="strip-cap">
          Hoofdstuk {done} van {total} verwerkt
        </p>
      </div>
    );
  }
  const first = book.first_chapter_index;
  const last = book.last_chapter_index;
  if (first === null || last === null) {
    return (
      <div className="book-strip-wrap">
        <ChapterStrip count={book.num_chapters || 30} cls={() => ""} />
        <p className="strip-cap">{book.job_error ? "Niet verwerkt" : "Wacht op verwerking"}</p>
      </div>
    );
  }
  let bm: number | null = null;
  try {
    const v = localStorage.getItem(`fabula.bookmark.${book.id}`);
    bm = v === null ? null : Number(v);
  } catch {
    /* per-reader convenience only */
  }
  const count = last - first + 1;
  const read = bm === null ? 0 : Math.min(count, Math.max(0, bm - first + 1));
  return (
    <div className="book-strip-wrap">
      <ChapterStrip count={count} cls={(i, n) => (i < Math.round((read / count) * n) ? "read" : "done")} />
      <p className="strip-cap">
        {bm === null ? "Nog niet begonnen" : `Gelezen t/m hoofdstuk ${Math.min(bm, last)}`}
      </p>
    </div>
  );
}

function Upload({ credits, onUploaded }: { credits: number; onUploaded: () => void }) {
  const { api } = useAuth();
  const legal = useLoad(() => api.legal(), [api]);
  const [file, setFile] = useState<File | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  // null = still looking (or no file); [] = nothing found.
  const [suggestions, setSuggestions] = useState<IsbnSuggestion[] | null>(null);
  const [isbn, setIsbn] = useState("");
  const [rights, setRights] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [processor, setProcessor] = useState("deepseek");
  // Estimate being recalculated for another processor (its price differs).
  const [repricing, setRepricing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  async function pick(f: File | null) {
    setFile(f);
    setEstimate(null);
    setSuggestions(null);
    setIsbn("");
    setError(null);
    setRights(false);
    setAcknowledged(false);
    if (!f) return;
    // Lookups are slower than the estimate: shown when they arrive, never blocking.
    api
      .isbnSuggestions(f)
      .then((r) => setSuggestions(r.suggestions))
      .catch(() => setSuggestions([]));
    setBusy(true);
    try {
      setEstimate(await api.estimate(f, processor));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const statement = legal.data?.rights_statement;
  const high = estimate?.provenance.findings.filter((f) => f.severity === "high") ?? [];
  const notes = estimate?.provenance.findings.filter((f) => f.severity !== "high") ?? [];
  const otherWarnings = (estimate?.warnings ?? []).filter((w) => !high.some((f) => f.message === w));
  const blocked = !!estimate?.refused;
  async function chooseProcessor(id: string) {
    setProcessor(id);
    if (!file) return;
    setRepricing(true);
    setError(null);
    try {
      setEstimate(await api.estimate(file, id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRepricing(false);
    }
  }

  const processorName = legal.data?.processors.find((p) => p.id === processor)?.name ?? processor;
  const canSubmit =
    !repricing &&
    estimate?.processor === processor &&
    !!statement && rights && (high.length === 0 || acknowledged) && !blocked && credits >= 1;

  async function submit() {
    if (!file || !statement) return;
    setBusy(true);
    setError(null);
    try {
      await api.upload(file, {
        rightsVersion: statement.version,
        provenanceAcknowledged: acknowledged,
        processor,
        isbn: isbn || undefined,
      });
      setFile(null);
      setEstimate(null);
      setSuggestions(null);
      setIsbn("");
      setRights(false);
      setAcknowledged(false);
      onUploaded();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (e instanceof Error && e.message.includes("rights statement has changed")) legal.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2>Boek toevoegen</h2>
      <p className="muted">
        Upload een EPUB van een boek dat je rechtmatig hebt verkregen. Verwerken kost 1 credit; je
        hebt er {credits}. Het bestand wordt na de verwerking verwijderd.
      </p>
      <label
        className={`dropzone ${dragging ? "dropzone-active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files?.[0];
          if (f) void pick(f);
        }}
      >
        <input
          type="file"
          accept=".epub,application/epub+zip"
          disabled={busy}
          onChange={(e) => void pick(e.target.files?.[0] ?? null)}
        />
        <span>
          {file ? <strong>{file.name}</strong> : <>Sleep een EPUB hierheen of <u>kies een bestand</u></>}
        </span>
      </label>
      {busy && !estimate && <Spinner label="Bezig…" />}
      <ErrorNote error={error} />
      {estimate && (
        <div className="stack">
          <dl className="facts-grid">
            <dt>Titel</dt>
            <dd>{estimate.title ?? file?.name}</dd>
            <dt>Auteur</dt>
            <dd>{estimate.authors.join(", ") || "onbekend"}</dd>
            <dt>Omvang</dt>
            <dd>
              {estimate.documents} delen, ±{Math.round(estimate.characters / 6 / 1000)}k woorden
            </dd>
            <dt>Geschatte kosten</dt>
            <dd>
              {repricing ? (
                <span className="muted">opnieuw berekenen voor {processorName}…</span>
              ) : (
                <>
                  {usd(estimate.estimate.cost_usd_with_cache)}–{usd(estimate.estimate.cost_usd_no_cache)}
                  {" "}
                  <span className="muted">
                    (harde limiet {usd(estimate.budget_usd)}; {processorName})
                  </span>
                </>
              )}
            </dd>
          </dl>

          {!blocked && (
            <IsbnChoice suggestions={suggestions} value={isbn} onChange={setIsbn} />
          )}

          {blocked && (
            <div className="note note-danger" role="alert">
              <strong>Dit bestand kan niet worden verwerkt.</strong> {refusal(estimate.refused ?? "")}
            </div>
          )}
          {!blocked && high.length > 0 && (
            <div className="note note-danger" role="alert">
              <strong>Let op: de herkomstcontrole vond problemen.</strong>
              <ul className="findings">
                {high.map((f) => (
                  <li key={f.code + (f.detail ?? "")}>
                    {f.message}
                    {f.detail && <span className="muted small"> ({f.detail})</span>}
                  </li>
                ))}
              </ul>
              <p className="small">
                Verwerk dit bestand alleen als je zeker weet dat je het rechtmatig hebt verkregen
                en mag laten verwerken. Je keuze wordt bij het boek vastgelegd.
              </p>
              <label className="check">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <span>Ik heb deze waarschuwing gelezen en wil toch doorgaan.</span>
              </label>
            </div>
          )}
          {(notes.length > 0 || otherWarnings.length > 0) && (
            <div className="note">
              <ul className="findings">
                {notes.map((f) => (
                  <li key={f.code + (f.detail ?? "")}>{f.message}</li>
                ))}
                {otherWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {!blocked && legal.data && (
            <label className="row">
              Verwerker (taalmodel)
              <select
                value={processor}
                disabled={repricing}
                onChange={(e) => void chooseProcessor(e.target.value)}
              >
                {legal.data.processors.map((p) => (
                  <option key={p.id} value={p.id} disabled={!p.available}>
                    {p.name} — {p.region}
                    {p.available ? "" : " (nog niet beschikbaar)"}
                  </option>
                ))}
              </select>
              <span className="muted small">
                {legal.data.processors.find((p) => p.id === processor)?.note}
              </span>
            </label>
          )}

          {blocked ? null : statement ? (
            <div>
              <strong>{statement.title}</strong>
              <ul className="statement small">
                {statement.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <label className="check">
                <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} />
                <span>
                  Ik verklaar dat dit alles klopt (zie de{" "}
                  <a href="#/voorwaarden" target="_blank" rel="noreferrer">
                    gebruiksvoorwaarden
                  </a>
                  ).
                </span>
              </label>
            </div>
          ) : (
            <ErrorNote error={legal.error} />
          )}
          {!blocked && (
            <div>
              <button className="btn" disabled={!canSubmit || busy} onClick={submit}>
                Verwerken (1 credit)
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Optional ISBN pick from the identification shortlist; "" = let identification decide. */
function IsbnChoice({
  suggestions,
  value,
  onChange,
}: {
  suggestions: IsbnSuggestion[] | null;
  value: string;
  onChange: (isbn: string) => void;
}) {
  if (suggestions === null) return <Spinner label="ISBN-suggesties zoeken…" />;
  if (suggestions.length === 0)
    return <p className="muted small">Geen ISBN-suggesties gevonden; FabulaWiki bepaalt het zelf.</p>;
  return (
    <fieldset className="isbn-choice">
      <legend>
        ISBN <span className="muted small">(optioneel)</span>
      </legend>
      <label className="check">
        <input type="radio" name="isbn" checked={value === ""} onChange={() => onChange("")} />
        <span>Geen keuze — laat FabulaWiki het bepalen</span>
      </label>
      {suggestions.map((s, i) => (
        <label className="check" key={s.isbn}>
          <input
            type="radio"
            name="isbn"
            checked={value === s.isbn}
            onChange={() => onChange(s.isbn)}
          />
          <span>
            <strong>{s.isbn}</strong> — {s.title}
            {s.authors.length > 0 && <>, {s.authors.join(", ")}</>}
            {(s.publisher || s.publish_year) && (
              <span className="muted">
                {" "}
                ({[s.publisher, s.publish_year].filter(Boolean).join(", ")})
              </span>
            )}
            {i === 0 && s.source !== "epub_metadata" && <span className="muted small"> · beste match</span>}
            {s.source === "epub_metadata" && <span className="muted small"> · uit het bestand</span>}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** Dutch wording for the server's refusal reasons (the API speaks English). */
function refusal(reason: string): string {
  if (reason.includes("removed after a notice"))
    return "Dit bestand (of dit ISBN) is verwijderd na een melding van de rechthebbende en kan niet opnieuw worden geüpload.";
  if (reason.startsWith("upload refused by the provenance check"))
    return "De herkomstcontrole weigert dit bestand (DRM, een voorbehoud voor tekst- en datamining of een illegale bron).";
  if (reason.includes("outside the service area"))
    return "FabulaWiki is (nog) niet beschikbaar in het land dat je hebt opgegeven.";
  return reason;
}

const COUNTRIES: [string, string][] = [
  ["NL", "Nederland"],
  ["BE", "België"],
  ["DE", "Duitsland"],
  ["FR", "Frankrijk"],
  ["LU", "Luxemburg"],
  ["AT", "Oostenrijk"],
  ["DK", "Denemarken"],
  ["ES", "Spanje"],
  ["IT", "Italië"],
  ["SE", "Zweden"],
  ["FI", "Finland"],
  ["IE", "Ierland"],
  ["PT", "Portugal"],
  ["PL", "Polen"],
  ["NO", "Noorwegen"],
  ["CH", "Zwitserland"],
  ["GB", "Verenigd Koninkrijk"],
  ["US", "Verenigde Staten"],
  ["CA", "Canada"],
];

function CountrySetting({ me, onChange }: { me: Me; onChange: () => void }) {
  const { api } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const known = COUNTRIES.some(([code]) => code === me.country);
  async function save(country: string) {
    setError(null);
    try {
      await api.updateMe({ country: country || null });
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  return (
    <label className="row">
      Land van verblijf
      <select value={me.country ?? ""} onChange={(e) => void save(e.target.value)}>
        <option value="">— kies —</option>
        {!known && me.country && <option value={me.country}>{me.country}</option>}
        {COUNTRIES.map(([code, name]) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </select>
      <ErrorNote error={error} />
    </label>
  );
}

function Takedowns() {
  const { api } = useAuth();
  const takedowns = useLoad(() => api.myTakedowns(), [api]);
  if (!takedowns.data?.length) return null;
  return (
    <div className="note note-danger" role="status">
      <strong>Verwijderd na een melding</strong>
      <ul className="findings">
        {takedowns.data.map((t) => (
          <li key={t.id}>
            <em>{t.title}</em> ({new Date(t.created_at).toLocaleDateString("nl-NL")}): {t.reason}
          </li>
        ))}
      </ul>
      <p className="small">
        Niet eens met dit besluit? Maak bezwaar via <a href="#/melden">het meldformulier</a> (soort
        "Overig").
      </p>
    </div>
  );
}

function Account({ me, onChange }: { me: Me; onChange: () => void }) {
  const { api, logout } = useAuth();
  const [error, setError] = useState<string | null>(null);

  async function download() {
    const data = await api.exportMe();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "fabulawiki-mijn-gegevens.json";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function remove() {
    const answer = window.prompt(
      "Je account, al je boeken en alle bijbehorende gegevens worden definitief verwijderd. " +
        "Typ VERWIJDEREN om te bevestigen.",
    );
    if (answer !== "VERWIJDEREN") return;
    try {
      await api.deleteMe();
      logout();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <details className="card account">
      <summary>
        <strong>Mijn account</strong>
      </summary>
      <div className="stack" style={{ marginTop: "0.75rem" }}>
        <CountrySetting me={me} onChange={onChange} />
      </div>
      <div className="row" style={{ marginTop: "0.75rem" }}>
        <button className="btn btn-ghost" onClick={() => void download()}>
          Mijn gegevens downloaden
        </button>
        <button className="btn btn-ghost btn-danger" onClick={() => void remove()}>
          Account verwijderen
        </button>
      </div>
      <ErrorNote error={error} />
    </details>
  );
}
